"use client";

interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: string;
}

export default function Icon({ name, className = "w-5 h-5", ...props }: IconProps) {
  // Common properties
  const svgProps = {
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className,
    ...props,
  };

  switch (name) {
    case "concrete":
      return (
        <svg {...svgProps}>
          {/* Isometric concrete formwork block with steel rebar rods extending */}
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
          {/* Rebar lines extending up */}
          <line x1="12" y1="2" x2="12" y2="4" stroke="currentColor" strokeWidth="1.5" />
          <line x1="7" y1="4.5" x2="7" y2="6.5" stroke="currentColor" strokeWidth="1.5" />
          <line x1="17" y1="4.5" x2="17" y2="6.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case "paint":
      return (
        <svg {...svgProps}>
          {/* Paint roller with dripping paint pattern and wet paint indicator */}
          <rect x="2" y="6" width="14" height="8" rx="2" />
          <path d="M6 14v4a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-4" />
          <path d="M16 10h3a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-3" />
          {/* Paint drips */}
          <path d="M4 14v1.5c0 0.8 0.4 1.2 0.8 1.2s0.8-0.4 0.8-1.2v-1.5" strokeWidth="1.5" />
          <path d="M10 14v1c0 0.8 0.4 1.2 0.8 1.2s0.8-0.4 0.8-1.2v-1" strokeWidth="1.5" />
        </svg>
      );
    case "flooring":
      return (
        <svg {...svgProps}>
          {/* Flooring planks with wood grain ticks and offset joint markings */}
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="9" y1="5" x2="9" y2="12" />
          <line x1="15" y1="12" x2="15" y2="19" />
          {/* Wood grain details */}
          <path d="M5 8.5h2.5M16 8.5h2.5M6 15.5h3.5M17 15.5h2" strokeWidth="1" strokeDasharray="2 2" />
        </svg>
      );
    case "area":
      return (
        <svg {...svgProps}>
          {/* Dashed square with dimension indicator arrows & center focal crosshair */}
          <rect x="6" y="6" width="12" height="12" rx="1" strokeDasharray="3 3" />
          <path d="M12 2v4M12 22v-4M2 12h4M22 12h-4" />
          <path d="m9 4 3-3 3 4M9 20l3 3 3-3M4 9l-3 3 3 3M20 9l3 3-3 3" />
          {/* Center focal crosshair */}
          <path d="M11 12h2M12 11v2" strokeWidth="1.5" />
        </svg>
      );
    case "fence":
      return (
        <svg {...svgProps}>
          {/* Picket fence with post caps and visible fastener points */}
          <path d="M4 9v11M9 9v11M14 9v11M19 9v11M2 12h20M2 17h20" />
          <path d="m2 9 2-3 2 3M7 9l2-3 2 3M12 9l2-3 2 3M17 9l2-3 2 3" />
          {/* Fastener dots on rails */}
          <circle cx="4" cy="12" r="0.75" fill="currentColor" />
          <circle cx="9" cy="12" r="0.75" fill="currentColor" />
          <circle cx="14" cy="12" r="0.75" fill="currentColor" />
          <circle cx="19" cy="12" r="0.75" fill="currentColor" />
          <circle cx="4" cy="17" r="0.75" fill="currentColor" />
          <circle cx="9" cy="17" r="0.75" fill="currentColor" />
          <circle cx="14" cy="17" r="0.75" fill="currentColor" />
          <circle cx="19" cy="17" r="0.75" fill="currentColor" />
        </svg>
      );
    case "mulch":
      return (
        <svg {...svgProps}>
          {/* Landscaping sprout with soil layer details and compost particles */}
          <path d="M12 22V10" />
          <path d="M12 10a9 9 0 0 1 9 9" />
          <path d="M12 10a9 9 0 0 0-9 9" />
          {/* Soil line and particles */}
          <path d="M5 20h14" />
          <circle cx="7" cy="21.5" r="0.5" fill="currentColor" />
          <circle cx="12" cy="21.5" r="0.5" fill="currentColor" />
          <circle cx="17" cy="21.5" r="0.5" fill="currentColor" />
        </svg>
      );
    case "drywall":
      return (
        <svg {...svgProps}>
          {/* Board pane grid with screw fastener patterns along the studs */}
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
          {/* Stud fastener dots */}
          <circle cx="9" cy="6" r="0.75" fill="currentColor" />
          <circle cx="9" cy="12" r="0.75" fill="currentColor" />
          <circle cx="9" cy="18" r="0.75" fill="currentColor" />
          <circle cx="15" cy="6" r="0.75" fill="currentColor" />
          <circle cx="15" cy="12" r="0.75" fill="currentColor" />
          <circle cx="15" cy="18" r="0.75" fill="currentColor" />
        </svg>
      );
    case "tile":
      return (
        <svg {...svgProps}>
          {/* Offset staggered tiles (subway brick tile layout) with spacer crosses */}
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
          <line x1="12" y1="3" x2="12" y2="9" />
          <line x1="7" y1="9" x2="7" y2="15" />
          <line x1="17" y1="9" x2="17" y2="15" />
          <line x1="12" y1="15" x2="12" y2="21" />
          {/* Spacer Crosses */}
          <path d="M11.5 9h1M12 8.5v1M6.5 15h1M7 14.5v1" strokeWidth="1" />
        </svg>
      );
    case "solar":
      return (
        <svg {...svgProps}>
          {/* Solar Panel with energy reflection rays */}
          <rect x="3" y="6" width="18" height="15" rx="1" />
          <line x1="3" y1="13.5" x2="21" y2="13.5" />
          <line x1="12" y1="6" x2="12" y2="21" />
          <line x1="7.5" y1="6" x2="4.5" y2="21" />
          <line x1="16.5" y1="6" x2="19.5" y2="21" />
          {/* Energy rays */}
          <path d="M7 2.5 8 4M12 2v2M17 2.5 16 4" strokeWidth="1.5" />
        </svg>
      );
    case "hvac":
      return (
        <svg {...svgProps}>
          {/* AC fan unit with temperature flow waves */}
          <circle cx="12" cy="12" r="9" />
          <path d="M12 12a3 3 0 1 0 0-6" />
          <path d="M12 12a3 3 0 1 0 0 6" />
          <path d="M12 12a3 3 0 1 0-6 0" />
          <path d="M12 12a3 3 0 1 0 6 0" />
          {/* Temperature flow wave details */}
          <path d="M3 3c1 1 1 2 2 2M21 21c-1-1-1-2-2-2" strokeWidth="1.5" />
        </svg>
      );
    case "water-heater":
      return (
        <svg {...svgProps}>
          <rect x="7" y="3" width="10" height="18" rx="3" />
          <path d="M10 7h4M9 18h6M12 3v-1M9 21v1M15 21v1" />
          <path d="M12 11c1.4 1.3 2 2.3 2 3.4a2 2 0 1 1-4 0c0-1.1.6-2.1 2-3.4Z" />
        </svg>
      );
    case "roofing":
      return (
        <svg {...svgProps}>
          {/* House roof frame with shingle texture details */}
          <path d="m2 11 10-9 10 9" />
          <path d="M4 11v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9" />
          {/* Shingles pattern overlays */}
          <path d="M7 11h10M6 14h12M5 17h14" strokeWidth="1" strokeDasharray="2 3" />
        </svg>
      );
    case "roof-shingles":
      return (
        <svg {...svgProps}>
          <path d="m2 10 10-7 10 7" />
          <path d="M5 10h14v10H5z" />
          <path d="M5 13h14M5 16h14M8 10v3M14 13v3M10 16v4M17 16v4" strokeWidth="1.4" />
        </svg>
      );
    case "siding":
      return (
        <svg {...svgProps}>
          <rect x="4" y="4" width="16" height="16" rx="1" />
          <path d="M4 8h16M4 12h16M4 16h16" />
          <path d="M8 4v4M14 8v4M10 12v4M16 16v4" strokeWidth="1.4" />
        </svg>
      );
    case "driveway":
      return (
        <svg {...svgProps}>
          <path d="M8 22 11 2h2l3 20" />
          <path d="M5 22h14M10 8h4M9 14h6" />
          <path d="M12 5v2M12 11v2M12 17v2" strokeWidth="1.2" />
        </svg>
      );
    case "home-addition":
      return (
        <svg {...svgProps}>
          <path d="m3 11 7-7 7 7" />
          <path d="M5 10v10h10V10" />
          <path d="M15 13h6v7h-6" />
          <path d="M18 15v3M16.5 16.5h3" />
        </svg>
      );
    case "pool":
      return (
        <svg {...svgProps}>
          <path d="M3 15c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 2-2" />
          <path d="M3 19c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 2-2" />
          <path d="M7 12V5a3 3 0 0 1 6 0v7M7 8h6" />
        </svg>
      );
    case "countertop":
      return (
        <svg {...svgProps}>
          <path d="M3 8h18v5H3z" />
          <path d="M6 13v7M18 13v7M5 20h14" />
          <path d="M8 5h8M10 3h4" />
          <path d="M14 10h4" strokeWidth="1.4" />
        </svg>
      );
    case "gutters":
      return (
        <svg {...svgProps}>
          <path d="M3 6h18l-2 5H5L3 6Z" />
          <path d="M19 11v7a3 3 0 0 1-3 3" />
          <path d="M6 15c1.5 0 1.5 1.5 3 1.5s1.5-1.5 3-1.5 1.5 1.5 3 1.5" strokeWidth="1.4" />
        </svg>
      );
    case "basement":
      return (
        <svg {...svgProps}>
          <path d="m3 10 9-7 9 7" />
          <path d="M5 10v10h14V10" />
          <path d="M5 15h14" />
          <path d="M8 18h8M9 13h.01M12 13h.01M15 13h.01" />
        </svg>
      );
    case "insulation":
      return (
        <svg {...svgProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M8 4v16M16 4v16M4 9h16M4 15h16" />
          <path d="M8 9c1 1 1 2 0 3M16 12c-1 1-1 2 0 3" strokeWidth="1.4" />
        </svg>
      );
    case "kitchen":
      return (
        <svg {...svgProps}>
          {/* Kitchen cooktop pot with steam heat lines */}
          <path d="M18 10h1a3 3 0 0 1 0 6h-1" />
          <path d="M2 10h16v8a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3Z" />
          {/* Steam vapour lines */}
          <path d="M5 3v4M10 2v5M15 3v4" strokeWidth="1.5" />
        </svg>
      );
    case "bathroom":
      return (
        <svg {...svgProps}>
          {/* Clawfoot tub with shower head spray droplets */}
          <path d="M2 12h18" />
          <path d="M2 12a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4" />
          <path d="M6 16v3M16 16v3" />
          {/* Shower pipe + head */}
          <path d="M18 12V5a1.5 1.5 0 0 0-1.5-1.5H15M15 3v2" />
          {/* Drops */}
          <circle cx="13" cy="6" r="0.5" fill="currentColor" />
          <circle cx="14" cy="8" r="0.5" fill="currentColor" />
        </svg>
      );
    case "cabinet":
      return (
        <svg {...svgProps}>
          <rect x="4" y="4" width="16" height="16" rx="1" />
          <path d="M12 4v16M4 10h16" />
          <circle cx="9" cy="15" r="0.75" fill="currentColor" />
          <circle cx="15" cy="15" r="0.75" fill="currentColor" />
          <path d="M7 7h2M15 7h2" strokeWidth="1.4" />
        </svg>
      );
    case "generator":
      return (
        <svg {...svgProps}>
          <rect x="3" y="7" width="18" height="11" rx="2" />
          <path d="M7 7V5h10v2M7 18v2M17 18v2" />
          <circle cx="8" cy="12.5" r="2" />
          <path d="M13 11h4M13 14h3" />
          <path d="m8 11.2-.8 1.4h1.4l-.8 1.4" strokeWidth="1.2" />
        </svg>
      );
    case "ev-charger":
      return (
        <svg {...svgProps}>
          <rect x="6" y="3" width="9" height="14" rx="2" />
          <path d="M9 7h3M10.5 17v4M15 9h2a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-2" />
          <path d="m10 10-1.5 2h2l-1.5 2" strokeWidth="1.3" />
        </svg>
      );
    case "retaining-wall":
      return (
        <svg {...svgProps}>
          <path d="M4 19h16" />
          <path d="M6 16h12M7 13h10M8 10h8M9 7h6" />
          <path d="M5 16v3M19 16v3M7 13v3M17 13v3M9 10v3M15 10v3" strokeWidth="1.3" />
          <path d="M3 7c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 6 2" strokeWidth="1.3" />
        </svg>
      );
    case "windows":
      return (
        <svg {...svgProps}>
          {/* Detailed sash window with framing, glass reflection highlights and sill */}
          <rect x="5" y="3" width="14" height="15" rx="1" />
          <line x1="5" y1="10" x2="19" y2="10" />
          <line x1="12" y1="3" x2="12" y2="18" />
          <line x1="3" y1="18" x2="21" y2="18" />
          <line x1="2" y1="20" x2="22" y2="20" />
          {/* Glass reflection lines */}
          <line x1="8" y1="5" x2="10" y2="7" strokeWidth="1" />
          <line x1="15" y1="12" x2="17" y2="14" strokeWidth="1" />
        </svg>
      );
    case "deck":
      return (
        <svg {...svgProps}>
          {/* Wood deck planks with screw/nail details and grain lines */}
          <path d="M2 17h20M2 11h20M2 5h20" />
          <line x1="6" y1="2" x2="6" y2="22" strokeDasharray="1 3" />
          <line x1="18" y1="2" x2="18" y2="22" strokeDasharray="1 3" />
          {/* Planks wood grain details */}
          <path d="M10 8h3M8 14h2M12 20h3" strokeWidth="1" strokeDasharray="1 2" />
        </svg>
      );
    case "roi":
      return (
        <svg {...svgProps}>
          {/* Return growth trend graph with dollar crosshair target */}
          <path d="m22 7-8.5 8.5-5-5L2 17" />
          <polyline points="16 7 22 7 22 13" />
          {/* Small return metric target */}
          <path d="M18 14.5v3M16.5 16h3" strokeWidth="1" />
          <circle cx="18" cy="16" r="2.5" />
        </svg>
      );
    case "calculator":
      return (
        <svg {...svgProps}>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="8" y1="6" x2="16" y2="6" />
          <line x1="16" y1="14" x2="16" y2="18" />
          <path d="M16 10h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01" />
        </svg>
      );
    case "check":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...svgProps}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case "lock":
      return (
        <svg {...svgProps}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      );
    case "microscope":
      return (
        <svg {...svgProps}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 11 2 2 4-4" />
        </svg>
      );
    case "mobile":
      return (
        <svg {...svgProps}>
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
      );
    case "sun":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
        </svg>
      );
    case "moon":
      return (
        <svg {...svgProps}>
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
        </svg>
      );
    case "info":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
    case "warning":
      return (
        <svg {...svgProps}>
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    default:
      return (
        <svg {...svgProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      );
  }
}
